import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useAdminStore } from "../../state/adminStore";
import { ConfirmDialog } from "./ConfirmDialog";
import { StatusPill } from "./StatusPill";
import type { InviteRow } from "../../lib/api";
import { capacityLabel, redemptionLabel } from "../../lib/inviteLabels";

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toISOString().slice(0, 16).replace("T", " ");
}

type StatusTone = "success" | "muted" | "warning";

function inviteStatus(row: InviteRow): { label: string; tone: StatusTone } {
  if (row.expires_at && new Date(row.expires_at).getTime() <= Date.now()) {
    return { label: "geschlossen", tone: "warning" };
  }
  // max_uses=0 means unlimited; otherwise check if we hit the cap.
  if (row.max_uses > 0 && row.redemption_count >= row.max_uses) {
    return { label: "erschöpft", tone: "muted" };
  }
  return { label: "offen", tone: "success" };
}

const INVITE_NAME_MAX = 80;

/** Registrier-URL für einen Invite-Code — wird vom CopyButton in die
 *  Zwischenablage kopiert, sodass der Admin den Link 1:1 verschicken kann
 *  und der Empfänger mit einem Tap auf /register?invite=… landet. */
function registerLinkFor(code: string): string {
  if (typeof window === "undefined") return `/register?invite=${code}`;
  return `${window.location.origin}/register?invite=${code}`;
}

interface CopyButtonProps {
  value: string;
  testid: string;
  children: ReactNode;
}

/** Small chip that copies `value` to clipboard on click and flashes
 *  "✓ Kopiert" for 1.5 s as visual feedback. */
function CopyButton({ value, testid, children }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  async function onClick() {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // Older browsers / locked-down WebViews: fall back to a transient
      // <textarea> + execCommand("copy"). Best-effort, no error UI.
      const ta = document.createElement("textarea");
      ta.value = value;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
      } catch {
        /* ignored — both paths failed, user can long-press the code */
      }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <button
      type="button"
      onClick={() => void onClick()}
      data-testid={testid}
      aria-live="polite"
      className={`inline-flex items-center min-h-[28px] px-2 rounded border text-[10px] uppercase tracking-wider font-medium transition-colors ${
        copied
          ? "border-success/40 text-success bg-success-soft"
          : "border-outline-soft text-fg-muted hover:text-accent hover:border-accent/40"
      }`}
    >
      {copied ? "✓ Kopiert" : children}
    </button>
  );
}

export function AdminInvitesTab() {
  const status = useAdminStore((s) => s.invitesStatus);
  const error = useAdminStore((s) => s.invitesError);
  const invites = useAdminStore((s) => s.invites);
  const pending = useAdminStore((s) => s.invitesPending);
  const loadInvites = useAdminStore((s) => s.loadInvites);
  const createInvite = useAdminStore((s) => s.createInvite);
  const expireInvite = useAdminStore((s) => s.expireInvite);
  const revokeInvite = useAdminStore((s) => s.revokeInvite);

  const [name, setName] = useState("");
  const [maxUses, setMaxUses] = useState<number>(1);
  const [unlimited, setUnlimited] = useState<boolean>(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    void loadInvites();
  }, [loadInvites]);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    setCreateError(null);
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      setCreateError("Name ist Pflichtfeld.");
      return;
    }
    const effectiveMaxUses = unlimited ? null : maxUses;
    if (!unlimited && (!Number.isInteger(maxUses) || maxUses < 1)) {
      setCreateError("Max. Verwendungen muss ≥ 1 sein (oder Unbegrenzt).");
      return;
    }
    try {
      await createInvite(trimmed, effectiveMaxUses);
      setName("");
      setMaxUses(1);
      setUnlimited(false);
    } catch {
      setCreateError("Konnte Invite nicht anlegen — siehe Banner unten.");
    }
  }

  return (
    <section className="space-y-4" data-testid="invites-tab">
      <form onSubmit={onCreate} className="space-y-3" data-testid="create-invite-form">
        <label className="block space-y-1.5">
          <span className="text-xs uppercase tracking-wider text-fg-faint">
            Name für den neuen Code <span className="text-danger">*</span>
          </span>
          <input
            type="text"
            maxLength={INVITE_NAME_MAX}
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={pending || status === "loading"}
            placeholder="z.B. Beta-Welle 1 — DLR Kollegen"
            data-testid="invite-name-input"
            className="w-full min-h-[44px] bg-bg border border-outline rounded-lg px-3 text-sm text-fg placeholder:text-fg-faint focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
            required
          />
        </label>

        <div className="flex flex-wrap items-end gap-3">
          <label className="block space-y-1.5 flex-1 min-w-[160px]">
            <span className="text-xs uppercase tracking-wider text-fg-faint">
              Max. Verwendungen
            </span>
            <input
              type="number"
              min={1}
              max={10000}
              value={maxUses}
              onChange={(e) => setMaxUses(Math.max(1, Math.floor(Number(e.target.value) || 1)))}
              disabled={unlimited || pending || status === "loading"}
              data-testid="invite-max-uses-input"
              className="w-full min-h-[44px] bg-bg border border-outline rounded-lg px-3 text-sm text-fg disabled:opacity-50 focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
            />
          </label>

          <label className="inline-flex items-center gap-2 text-sm text-fg-soft min-h-[44px] shrink-0">
            <input
              type="checkbox"
              checked={unlimited}
              onChange={(e) => setUnlimited(e.target.checked)}
              disabled={pending}
              data-testid="invite-unlimited-checkbox"
              className="size-4 rounded border-outline bg-bg accent-accent"
            />
            Unbegrenzt
          </label>

          <button
            type="submit"
            disabled={pending || status === "loading" || name.trim().length === 0}
            data-testid="create-invite"
            className="min-h-[44px] px-4 rounded-lg bg-accent text-accent-on text-sm font-semibold shadow-card disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-outline-strong shrink-0"
          >
            {pending ? "…" : "+ Neuer Code"}
          </button>
        </div>

        {createError && (
          <div className="text-xs text-danger" role="alert">{createError}</div>
        )}
      </form>

      <div className="text-sm font-semibold text-fg-soft">
        {status === "ready" ? `Invites · ${invites.length}` : "Invites"}
      </div>

      {status === "loading" && (
        <p className="text-fg-muted text-sm animate-pulse">Lade Invites…</p>
      )}
      {status === "error" && (
        <div className="bg-danger-soft border border-danger/40 rounded-xl px-4 py-3 text-danger text-sm" role="alert">
          Fehler beim Laden: {error}
        </div>
      )}
      {status === "ready" && invites.length === 0 && (
        <div className="bg-card border border-outline rounded-2xl p-5 text-fg-muted text-sm">
          Noch keine Invites — gib dem nächsten einen Namen und klick auf <strong className="text-fg">+ Neuer Code</strong>.
        </div>
      )}

      {/* Desktop table */}
      <div className="hidden sm:block bg-card border border-outline rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-bg/40">
            <tr className="text-left text-xs uppercase tracking-wider text-fg-faint">
              <th className="px-4 py-2 font-medium">Name</th>
              <th className="px-4 py-2 font-medium">Code</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 font-medium">Eingelöst</th>
              <th className="px-4 py-2 font-medium">Übrig</th>
              <th className="px-4 py-2 font-medium">Erstellt</th>
              <th className="px-4 py-2 font-medium text-right">Aktion</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-outline-soft">
            {invites.map((row) => {
              const s = inviteStatus(row);
              // "active" = the row should be actionable. Closed (expires_at past)
              // or exhausted (count >= max_uses with max>0) → no action.
              const active = s.tone === "success";
              return (
                <tr key={row.code} className="hover:bg-bg/40 transition-colors" data-testid={`invite-row-${row.code}`}>
                  <td className="px-4 py-3 text-fg-soft">
                    {row.name ? row.name : <span className="text-fg-faint italic">(kein Name)</span>}
                  </td>
                  <td className="px-4 py-3 font-mono text-fg-soft text-xs">
                    <div className="space-y-1.5">
                      <div className="break-all">{row.code}</div>
                      <div className="flex gap-1 flex-wrap">
                        <CopyButton value={row.code} testid={`copy-code-${row.code}`}>
                          Code
                        </CopyButton>
                        <CopyButton
                          value={registerLinkFor(row.code)}
                          testid={`copy-link-${row.code}`}
                        >
                          Link
                        </CopyButton>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill tone={s.tone}>{s.label}</StatusPill>
                  </td>
                  <td className="px-4 py-3 text-fg-soft text-xs tabular" data-testid={`redemption-count-${row.code}`}>
                    {redemptionLabel(row)}
                  </td>
                  <td className="px-4 py-3 text-fg-muted tabular text-xs" data-testid={`capacity-${row.code}`}>
                    {capacityLabel(row)}
                  </td>
                  <td className="px-4 py-3 text-fg-muted tabular text-xs">{fmtDate(row.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    {active && row.redemption_count === 0 ? (
                      // No redemption yet → nothing to audit → hard delete is fine.
                      <ConfirmDialog
                        title="Invite widerrufen?"
                        description={
                          <span>
                            <span className="font-mono">{row.code}</span> ({row.name || "kein Name"}) wurde noch nicht eingelöst und wird komplett aus der Datenbank entfernt.
                          </span>
                        }
                        confirmLabel="Widerrufen"
                        onConfirm={() => revokeInvite(row.code)}
                        trigger={
                          <button
                            className="min-h-[36px] px-3 rounded-lg text-danger text-xs font-medium hover:bg-danger-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-danger/60"
                            data-testid={`revoke-${row.code}`}
                          >
                            Widerrufen
                          </button>
                        }
                      />
                    ) : active ? (
                      // Code has ≥1 redemption — preserve audit, just close it.
                      <ConfirmDialog
                        title="Invite schließen?"
                        description={
                          <span>
                            <span className="font-mono">{row.code}</span> ({row.name || "kein Name"}) wird sofort geschlossen. Niemand kann sich damit mehr registrieren. Die Zeile bleibt für den Audit-Trail erhalten.
                          </span>
                        }
                        confirmLabel="Schließen"
                        onConfirm={() => expireInvite(row.code)}
                        trigger={
                          <button
                            className="min-h-[36px] px-3 rounded-lg text-warning text-xs font-medium hover:bg-warning-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-warning/60"
                            data-testid={`expire-${row.code}`}
                          >
                            Schließen
                          </button>
                        }
                      />
                    ) : (
                      <span className="text-fg-faint text-xs">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="sm:hidden space-y-2">
        {invites.map((row) => {
          const s = inviteStatus(row);
          const active = s.tone === "success";
          return (
            <div
              key={row.code}
              data-testid={`invite-card-${row.code}`}
              className={`bg-card border border-outline rounded-xl p-4 space-y-2 ${!active ? "opacity-80" : ""}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-fg break-words">
                    {row.name || <span className="text-fg-faint italic">(kein Name)</span>}
                  </div>
                  <div className="font-mono text-xs text-fg-soft break-all">{row.code}</div>
                  <div className="flex gap-1 mt-1.5">
                    <CopyButton value={row.code} testid={`copy-code-mobile-${row.code}`}>
                      Code
                    </CopyButton>
                    <CopyButton
                      value={registerLinkFor(row.code)}
                      testid={`copy-link-mobile-${row.code}`}
                    >
                      Link
                    </CopyButton>
                  </div>
                </div>
                <StatusPill tone={s.tone}>{s.label}</StatusPill>
              </div>
              <div className="flex items-center justify-between text-xs text-fg-muted tabular">
                <span>Erstellt: {fmtDate(row.created_at)}</span>
                <span data-testid={`redemption-count-mobile-${row.code}`}>
                  Eingelöst: {redemptionLabel(row)}
                </span>
              </div>
              <div className="text-xs text-fg-muted tabular" data-testid={`capacity-mobile-${row.code}`}>
                Übrig: {capacityLabel(row)}
              </div>
              {row.last_redeemed_at && (
                <div className="text-xs text-fg-muted">
                  Zuletzt: {fmtDate(row.last_redeemed_at)}
                </div>
              )}
              {active && row.redemption_count === 0 ? (
                <ConfirmDialog
                  title="Invite widerrufen?"
                  description={<span>Code wurde noch nicht eingelöst — wird komplett entfernt.</span>}
                  confirmLabel="Widerrufen"
                  onConfirm={() => revokeInvite(row.code)}
                  trigger={
                    <button
                      className="w-full min-h-[44px] mt-1 rounded-lg border border-danger/40 text-danger text-sm font-medium hover:bg-danger-soft"
                      data-testid={`revoke-mobile-${row.code}`}
                    >
                      Widerrufen
                    </button>
                  }
                />
              ) : active ? (
                <ConfirmDialog
                  title="Invite schließen?"
                  description={<span>Niemand kann sich mehr damit registrieren. Audit bleibt erhalten.</span>}
                  confirmLabel="Schließen"
                  onConfirm={() => expireInvite(row.code)}
                  trigger={
                    <button
                      className="w-full min-h-[44px] mt-1 rounded-lg border border-warning/40 text-warning text-sm font-medium hover:bg-warning-soft"
                      data-testid={`expire-mobile-${row.code}`}
                    >
                      Schließen
                    </button>
                  }
                />
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}
